import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BACKEND_URL } from '../constants';
import { AuthErrorCode, UserPreferences } from '@axys-notes/common';
import { useTeamsContext } from '../providers/teams-context-provider';
import { ApiError, handleErrorResponse, handleRetry } from './common';

export const useUserPreferencesMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();

  const putPreferences = async (query: UserPreferences) => {
    const response = await fetch(`${BACKEND_URL}/api/user/preferences`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: teamsContext.authCode
          ? `Bearer ${teamsContext.authCode}`
          : '',
      },
      body: JSON.stringify(query),
    });

    await handleErrorResponse(response, teamsContext);
  };

  return useMutation<void, Error, UserPreferences>({
    mutationKey: ['user-preferences'],
    mutationFn: async (query: UserPreferences) => {
      try {
        await putPreferences(query);
      } catch (error) {
        if (
          !(error instanceof ApiError) ||
          error.code !== AuthErrorCode.ConsentRequired ||
          !error.consent
        ) {
          throw error;
        }
        // Wait for the consent flow it triggered, then replay once.
        try {
          await error.consent;
        } catch {
          throw error;
        }
        await putPreferences(query);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['user-preferences-get'],
      });
    },
  });
};

export const useGetUserPreferencesQuery = () => {
  const teamsContext = useTeamsContext();
  return useQuery<UserPreferences>({
    enabled:
      !!teamsContext.authCode &&
      teamsContext.consent &&
      !teamsContext.consentInProgress,
    queryKey: ['user-preferences-get'],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 10000,
    queryFn: async () => {
      const response = await fetch(`${BACKEND_URL}/api/user/preferences`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: teamsContext.authCode
            ? `Bearer ${teamsContext.authCode}`
            : '',
        },
      });
      await handleErrorResponse(response, teamsContext);
      return response.json();
    },
    retry: (failureCount: number, error: Error) =>
      handleRetry(failureCount, error, teamsContext),
  });
};
